import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  ResponseType,
} from "axios";

export interface FullRequestParams
  extends Omit<AxiosRequestConfig, "data" | "params" | "url" | "responseType"> {
  secure?: boolean;
  path: string;
  type?: ContentType;
  query?: QueryParamsType;
  format?: ResponseType;
  body?: unknown;
}

export type RequestParams = Omit<
  FullRequestParams,
  "body" | "method" | "query" | "path"
>;

export interface ApiConfig<SecurityDataType = unknown>
  extends Omit<AxiosRequestConfig, "data" | "cancelToken"> {
  securityWorker?: (
    securityData: SecurityDataType | null
  ) => Promise<AxiosRequestConfig | void> | AxiosRequestConfig | void;
  secure?: boolean;
  format?: ResponseType;
}

export enum ContentType {
  Json = "application/json",
  FormData = "multipart/form-data",
  UrlEncoded = "application/x-www-form-urlencoded",
}

export type QueryParamsType = Record<string | number, any>;

export interface EmailEngagementStatisticsGroupedRequestView {
  from?: number;

  to?: number;

  eventTypes?: (
    | "CLICK"
    | "UNIQUE_CLICK"
    | "OPEN"
    | "UNIQUE_OPEN"
    | "SEND"
    | "UNSUBSCRIBE"
    | "DELIVERED"
    | "HARD_BOUNCED"
    | "SOFT_BOUNCED"
    | "SPAM"
    | "NOT_SPAM"
  )[];

  groupTimeUnit?: "HOUR" | "DAY" | "MONTH" | "YEAR";

  activityTypes: ("WORKFLOW" | "CAMPAIGN" | "AUTOMATION")[];

  email?: string;
}

export interface PNRatioRequestView {
  from?: number;

  to?: number;

  activityTypes: ("WORKFLOW" | "CAMPAIGN" | "AUTOMATION")[];
}

export interface PNRatioResponseView {
  pnratio: number;
}

export interface EmailEngagementStatisticsGroupedResponseView {
  currentStats: {
    send: number;
    delivered: number;
    uniqueOpen: number;
    uniqueClick: number;
    unsubscribe: number;
    sendPercent: number;
    deliveredPercent: number;
    uniqueOpenPercent: number;
    uniqueClickedPercent: number;
    unsubscribedPercent: number;
  };
  previousStats: {
    send: number;
    delivered: number;
    uniqueOpen: number;
    uniqueClick: number;
    unsubscribe: number;
    sendPercent: number;
    deliveredPercent: number;
    uniqueOpenPercent: number;
    uniqueClickedPercent: number;
    unsubscribedPercent: number;
  };
  compareStats: {
    compareSend: number;
    compareDelivered: number;
    compareUniqueOpen: number;
    compareUniqueClick: number;
    compareUnsubscribe: number;
    compareSendPercent: number;
    compareDeliveredPercent: number;
    compareUniqueOpenPercent: number;
    compareUniqueClickedPercent: number;
    compareUnsubscribedPercent: number;
  };
  compareDays: number;
}

export interface OpenClickEventStatistics {
  eventType: string;
  eventTime: number;
  currentCount: number;
  previousCount: number;
}

export interface UniqueOpenGroupedStatistics {
  statistics: OpenClickEventStatistics[];
  currentTotal: number;
  previousTotal: number;
  comparePercent: number;
}

export interface UniqueClickedGroupedStatistics {
  statistics: OpenClickEventStatistics[];
  currentTotal: number;
  previousTotal: number;
  comparePercent: number;
}

export interface TrafficAwarenessStatisticsGroupedResponseView {
  uniqueOpenGroupedStatistics: UniqueOpenGroupedStatistics;
  uniqueClickedGroupedStatistics: UniqueClickedGroupedStatistics;
  compareDays: number;
}

export interface SiteInteractionStatistics {
  siteInteractionStats: OpenClickEventStatistics[];
  currentNumberOfFormSubmissions: number;
  previousNumberOfFormSubmissions: number;
  compareFormSubmissionsPercent: number;
}
export interface SiteInteractionStatisticsGroupedResponseView {
  siteInteractionStatistics: SiteInteractionStatistics;
  compareDays: number;
}

export interface SiteInteractionStatisticsGroupedRequestView {
  from?: number;

  to?: number;

  groupTimeUnit?: "HOUR" | "DAY" | "MONTH" | "YEAR";
}
export interface TrafficAwarenessStatisticsGroupedRequestView {
  from?: number;

  to?: number;

  eventTypes?: ("UNIQUE_CLICK" | "UNIQUE_OPEN")[];

  groupTimeUnit?: "HOUR" | "DAY" | "MONTH" | "YEAR";
}

export interface TopEngagedItem {
  name: string;
  sent: number;
  delivered: number;
  open: number;
  clicked: number;
  spam: number;
  unsubscribed: number;
  deliveredProgress: number;
  openProgress: number;
  clickedProgress: number;
  spamProgress: number;
  unsubscribedProgress: number;
  pnratio: number;
}

export interface TopEngagedCampaignsResponseView {
  topEngagedCampaigns: TopEngagedItem[];
}

export interface TopEngagedWorkflowsResponseView {
  topEngagedWorkflows: TopEngagedItem[];
}
export interface AccountInsightView {
  id: string;
  name: string;
  eventTime: number;
  accountInsightsType:
    | "CAMPAIGN"
    | "WORKFLOW"
    | "FORM"
    | "AUDIENCE"
    | "SEGMENT";
  eventType:
    | "CREATED"
    | "UPDATED"
    | "DELETED"
    | "ACTIVE"
    | "INACTIVE"
    | "QUEUED"
    | "LAUNCHED"
    | "COMPLETED"
    | "SCHEDULED"
    | "FAILED"
    | "PAUSED"
    | "RECURRING"
    | "STOPPED"
    | "UPLOADED"
    | "IN_PROGRESS";
  creationSource: "AI" | "API" | "UI";
}

export interface CustomerActivityView {
  activityId: string;
  eventEmail: string;
  eventTime: number;
  customerActivityType:
    | "OPEN"
    | "CLICK"
    | "UNSUBSCRIBE"
    | "ORDER"
    | "FORM_SUBMISSION";
  activityType: "CAMPAIGN" | "WORKFLOW" | "FORM";
  name: string;
}

export interface Sort {
  sorted?: boolean;

  unsorted?: boolean;

  empty?: boolean;
}

export interface Pageable {
  page?: number;

  size?: number;

  sort?: string[];
}

export interface AccountInsightsResponseView {
  totalPages?: number;

  totalElements?: number;

  sort?: Sort;

  numberOfElements?: number;

  pageable?: Pageable;

  first?: boolean;

  last?: boolean;

  size?: number;

  content?: AccountInsightView[];

  number?: number;

  empty?: boolean;
}

export interface CustomerActivityResponseView {
  totalPages?: number;

  totalElements?: number;

  sort?: Sort;

  numberOfElements?: number;

  pageable?: Pageable;

  first?: boolean;

  last?: boolean;

  size?: number;

  content?: CustomerActivityView[];

  number?: number;

  empty?: boolean;
}

export interface AccountInsightsFilterView {
  accountInsightsTypes?: (
    | "CAMPAIGN"
    | "WORKFLOW"
    | "FORM"
    | "AUDIENCE"
    | "SEGMENT"
  )[];
}

export interface CustomerActivityFilterView {
  customerActivityType?: (
    | "OPEN"
    | "CLICK"
    | "UNSUBSCRIBE"
    | "ORDER"
    | "FORM_SUBMISSION"
  )[];
  email?: string;
}

export interface TotalRevenueRequestView {
  from?: number;

  to?: number;
}

export interface TotalOrderRequestView {
  from?: number;

  to?: number;
}

export interface TotalRevenueResponseView {
  campaignRevenue: {
    currentRevenue: number;
    previousRevenue: number;
    comparePercent: number;
  };
  workflowRevenue: {
    currentRevenue: number;
    previousRevenue: number;
    comparePercent: number;
  };
  totalRevenue: {
    currentRevenue: number;
    previousRevenue: number;
    comparePercent: number;
  };
  compareDays: number;
}

export interface TotalOrderResponseView {
  campaignOrders: {
    currentOrders: number;
    previousOrders: number;
    comparePercent: number;
  };
  workflowOrders: {
    currentOrders: number;
    previousOrders: number;
    comparePercent: number;
  };
  totalOrders: {
    currentOrders: number;
    previousOrders: number;
    comparePercent: number;
  };
  compareDays: number;
}

export interface ContactHistoryOverviewFilterView {
  from?: number;

  to?: number;

  email?: string;
}
export interface ContactHistoryOverview {
  email: string;
  firstName: string;
  lastName: string;
  status: string;
  lastSent: number;
  lastOpen: number;
  lastClick: number;
  lastDelivered: number;
  lastUnsubscribe: number;
  lastSpam: number;
  blacklisted: boolean;
  bounced: boolean;
}

export interface ContactHistoryFilterView {
  from?: number;
  to?: number;
  email?: string;
}

export interface ContactHistoryView {
  day: number;
  sent: number;
  delivered: number;
  uniqueOpens: number;
  uniqueClicks: number;
  softBounce: number;
  hardBounce: number;
  unsubscribe: number;
  spam: number;
  blocked: number;
  suppressed: number;
  deliveryRate: number;
  openRate: number;
  clickRate: number;
  clickToOpenRate: number;
  unsubscribeRate: number;
  spamRate: number;
  bounceRate: number;
}

export interface ContactHistoryPageResponse {
  content?: ContactHistoryView[];
  pageable?: Pageable;
  totalElements?: number;
  last?: boolean;
  totalPages?: number;
  first?: boolean;
  sort?: Sort[];
  size?: number;
  number?: number;
  numberOfElements?: number;
  empty?: boolean;
}

export interface AiSummaryResponseView {
  summary?: string;
  placeholder?: string;
}

export interface CustomerAudienceMembershipFilterView {
  email: string;
}

export interface AudienceMembershipView {
  segmentId: string;
  segmentName: string;
  segmentType: "UPLOAD" | "INTEGRATION" | "FORM";
}

export interface SuppressionMembershipView {
  suppressionId: number;
  suppressionName: string;
  suppressionScope: "LOCAL" | "GLOBAL";
}

export interface CustomerAudienceMembershipView {
  customerId: string;
  email: string;
  audiences: AudienceMembershipView[];
  suppressed: boolean;
  suppressions: SuppressionMembershipView[];
  suppressionCheckFailed: boolean;
}

export interface SegmentFilterView {
  searchTextName?: string;
  segmentTypes?: ("UPLOAD" | "INTEGRATION" | "FORM")[];
  aiEnabled?: boolean;
}

export interface SegmentView {
  id: string;
  name: string;
  segmentType: "UPLOAD" | "INTEGRATION" | "FORM";
  numberOfCustomers: number;
  createdAt: number;
  updatedAt: number;
  status: string;
  aiEnabled: boolean;
  cleaned: boolean;
}

export interface SegmentPageResponse {
  content?: SegmentView[];
  pageable?: Pageable;
  totalElements?: number;
  last?: boolean;
  totalPages?: number;
  first?: boolean;
  sort?: Sort;
  size?: number;
  number?: number;
  numberOfElements?: number;
  empty?: boolean;
}

export default abstract class GeneratedApi {
  abstract request<T>({
    secure,
    path,
    type,
    query,
    format,
    body,
    appName,
    ...params
  }: FullRequestParams & { appName?: string }): Promise<AxiosResponse<T>>;

  postTrafficAwarenessStatisticsGrouped(
    data: TrafficAwarenessStatisticsGroupedRequestView,
    params: RequestParams = {}
  ) {
    return this.request<TrafficAwarenessStatisticsGroupedResponseView>({
      path: "/dashboard/metrics/overview",
      method: "POST",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  postSiteInteractionStatisticsGrouped(
    data: SiteInteractionStatisticsGroupedRequestView,
    params: RequestParams = {}
  ) {
    return this.request<SiteInteractionStatisticsGroupedResponseView>({
      path: "/dashboard/site-interaction",
      method: "POST",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  postEmailEngagementStatisticsGrouped(
    data: EmailEngagementStatisticsGroupedRequestView,
    params: RequestParams = {}
  ) {
    return this.request<EmailEngagementStatisticsGroupedResponseView>({
      path: "/dashboard/email-engagement",
      method: "POST",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  getPNRatio(data: PNRatioRequestView, params: RequestParams = {}) {
    return this.request<PNRatioResponseView>({
      path: "/dashboard/PN-ratio",
      method: "POST",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  getTopEngagedCampaigns(params: RequestParams = {}) {
    return this.request<TopEngagedCampaignsResponseView>({
      path: "/dashboard/campaigns/top-engaged",
      method: "GET",

      type: ContentType.Json,
      ...params,
    });
  }

  getTopEngagedWorkflows(params: RequestParams = {}) {
    return this.request<TopEngagedWorkflowsResponseView>({
      path: "/dashboard/workflows/top-engaged",
      method: "GET",

      type: ContentType.Json,
      ...params,
    });
  }

  postAccountInsightsFindFilter(
    data: AccountInsightsFilterView,
    query: {
      page?: number;
      size?: number;
      sort?: string[];
    },
    params: RequestParams = {}
  ) {
    return this.request<AccountInsightsResponseView>({
      path: "/dashboard/account-insights",
      method: "POST",
      query: query,
      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  postCustomerActivityFindFilter(
    data: CustomerActivityFilterView,
    query: {
      page?: number;
      size?: number;
      sort?: string[];
    },
    params: RequestParams = {}
  ) {
    return this.request<CustomerActivityResponseView>({
      path: "/dashboard/customer-activity",
      method: "POST",
      query: query,
      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  postTotalRevenue(data: TotalRevenueRequestView, params: RequestParams = {}) {
    return this.request<TotalRevenueResponseView>({
      path: "/dashboard/top-revenue",
      method: "POST",
      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  postTotalOrders(data: TotalOrderRequestView, params: RequestParams = {}) {
    return this.request<TotalOrderResponseView>({
      path: "/dashboard/top-order",
      method: "POST",
      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  putContactHistoryOverview(
    data: ContactHistoryOverviewFilterView,
    params: RequestParams = {}
  ) {
    return this.request<ContactHistoryOverview>({
      path: "/contact-history/overview",
      method: "PUT",
      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  putEmailEngagementStatistics(
    data: EmailEngagementStatisticsGroupedRequestView,
    params: RequestParams = {}
  ) {
    return this.request<EmailEngagementStatisticsGroupedResponseView>({
      path: "/contact-history/email-engagement",
      method: "PUT",

      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  putContactHistoryFilter(
    data: ContactHistoryFilterView,
    query: {
      page?: number;
      size?: number;
      sort?: string[];
    },
    params: RequestParams = {}
  ) {
    return this.request<ContactHistoryPageResponse>({
      path: "/contact-history",
      method: "PUT",
      query: query,
      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  getDashboardAiSummary(params: RequestParams = {}) {
    return this.request<AiSummaryResponseView>({
      path: "/dashboard/ai-summary",
      method: "GET",

      type: ContentType.Json,
      ...params,
    });
  }

  putCustomerAudienceMembership(
    data: CustomerAudienceMembershipFilterView,
    params: RequestParams = {}
  ) {
    return this.request<CustomerAudienceMembershipView>({
      path: "/customer/audience-membership",
      method: "PUT",
      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  putSegmentFindFilter(
    data: SegmentFilterView,
    query: {
      page?: number;
      size?: number;
      sort?: string[];
      countAllCustomers?: boolean;
    },
    params: RequestParams = {}
  ) {
    return this.request<SegmentPageResponse>({
      path: "/segment/find/filter",
      method: "PUT",
      query: query,
      body: data,
      type: ContentType.Json,
      ...params,
    });
  }

  postSegmentCustomerBySegmentIdAndCustomerId(
    segmentId: string,
    customerId: string,
    params: RequestParams = {}
  ) {
    return this.request<void>({
      path: `/segment/${segmentId}/customer/${customerId}`,
      method: "POST",
      type: ContentType.Json,
      ...params,
    });
  }

  deleteSegmentCustomerBySegmentIdAndCustomerId(
    segmentId: string,
    customerId: string,
    params: RequestParams = {}
  ) {
    return this.request<void>({
      path: `/segment/${segmentId}/customer/${customerId}`,
      method: "DELETE",
      type: ContentType.Json,
      ...params,
    });
  }
}
